/**
 * Money is always an integer number of minor units (cents). Never a float.
 * `MoneyMinor` values are produced by `toMinor()` and consumed by `fromMinor()`.
 */
export type MoneyMinor = number;

export const MINOR_UNITS_PER_MAJOR = 100;

const MAX_REASONABLE_MINOR = 1_000_000_000_00; // 1 billion major units

/** Thrown when a monetary value cannot be represented precisely. */
export class MoneyParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyParseError';
  }
}

/**
 * Parse a user supplied amount into minor units. Accepts strings or numbers but
 * rejects values with more than two decimal places so rounding can never be
 * used to silently alter a financial figure.
 */
export function toMinor(input: string | number): MoneyMinor {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) throw new MoneyParseError('Amount must be a finite number');
    const minor = Math.round(input * MINOR_UNITS_PER_MAJOR);
    if (Math.abs(minor / MINOR_UNITS_PER_MAJOR - input) > 1e-9) {
      throw new MoneyParseError('Amount has too many decimal places');
    }
    return validateMinor(minor);
  }

  const raw = input.trim().replace(/,/g, '');
  if (raw === '') throw new MoneyParseError('Amount is required');
  if (!/^-?\d+(\.\d{1,2})?$/.test(raw)) {
    throw new MoneyParseError('Amount must be a number with at most 2 decimal places');
  }
  const [whole, fraction = ''] = raw.replace('-', '').split('.');
  const minor = Number(whole) * MINOR_UNITS_PER_MAJOR + Number(fraction.padEnd(2, '0'));
  return validateMinor(raw.startsWith('-') ? -minor : minor);
}

export function validateMinor(minor: MoneyMinor): MoneyMinor {
  if (!Number.isInteger(minor)) throw new MoneyParseError('Amount must be a whole number of cents');
  if (Math.abs(minor) > MAX_REASONABLE_MINOR) throw new MoneyParseError('Amount is out of range');
  return minor;
}

export function fromMinor(minor: MoneyMinor): number {
  return minor / MINOR_UNITS_PER_MAJOR;
}

export const CURRENCY_CODES = ['USD', 'ZIG'] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];

export const CURRENCY_NAMES: Record<CurrencyCode, string> = {
  USD: 'US Dollar',
  ZIG: 'ZiG (Zimbabwe Gold)',
};

/** Both currencies render with a plain `$`; the code distinguishes them. */
const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: '$',
  ZIG: '$',
};

/**
 * Legacy single-currency codes. The school's old pseudo-currency becomes
 * dollars, and ZiG is the new second currency.
 */
const LEGACY_CURRENCY_CODES: Record<string, CurrencyCode> = {
  ZUSD: 'USD',
  ZWL: 'USD',
};

const CURRENCY_LOCALES: Record<string, string> = {
  USD: 'en-US',
  ZAR: 'en-ZA',
  GBP: 'en-GB',
  EUR: 'de-DE',
};

export function isCurrencyCode(input: string): input is CurrencyCode {
  return (CURRENCY_CODES as readonly string[]).includes(input);
}

/** Maps a user supplied code (case/whitespace tolerant, incl. legacy ZUSD/ZWL) onto USD or ZIG. */
export function normalizeCurrency(input: string | null | undefined): CurrencyCode | undefined {
  if (!input) return undefined;
  const code = input.trim().toUpperCase();
  if (isCurrencyCode(code)) return code;
  return LEGACY_CURRENCY_CODES[code];
}

/** Display symbol for a code. Both supported currencies are `$`; unknown codes fall back to the code. */
export function currencySymbol(input: string | null | undefined): string {
  const code = normalizeCurrency(input ?? 'USD');
  if (!code) return input ? `${input} ` : '$';
  return CURRENCY_SYMBOLS[code];
}

function decimalPart(minor: MoneyMinor, decimals: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(fromMinor(Math.abs(minor)));
}

/**
 * ZiG is not ISO-4217, so Intl currency style is never used for either of the
 * two supported codes; the string is built by hand and always prefixed `$`.
 */
export function formatMoney(minor: MoneyMinor, currency: string = 'USD', decimals = 2): string {
  const code = normalizeCurrency(currency) ?? currency;
  const symbol = CURRENCY_SYMBOLS[code as CurrencyCode];
  if (symbol) {
    return `${minor < 0 ? '-' : ''}${symbol}${decimalPart(minor, decimals)}`;
  }
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[code] ?? 'en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(fromMinor(minor));
  } catch {
    // Unknown code: fall through to the plain formatter.
  }
  return `${minor < 0 ? '-' : ''}${decimalPart(minor, decimals)} ${code}`;
}

/** Explicit form for receipts, statements and mixed-currency summaries. */
export function formatMoneyWithCode(minor: MoneyMinor, currency: string = 'USD', decimals = 2): string {
  return `${formatMoney(minor, currency, decimals)} ${normalizeCurrency(currency) ?? currency}`;
}

/**
 * `rate` is ZiG per 1 USD, entered by an administrator, and is only ever used
 * for explicitly-labelled converted views - never for ledger maths.
 */
export function convertMinor(minor: MoneyMinor, from: CurrencyCode, to: CurrencyCode, rate: number): MoneyMinor {
  if (from === to) return minor;
  if (!Number.isFinite(rate) || rate <= 0) throw new MoneyParseError('Conversion rate must be a positive number');
  const converted = from === 'USD' ? minor * rate : minor / rate;
  return Math.round(converted);
}

/**
 * Expresses an amount in the reporting base currency. Its only legitimate uses
 * are labelled roll-ups and policy gates (an approval threshold is one number,
 * so a ZiG figure cannot be compared against it as raw minor units) - it never
 * rewrites what the ledger holds in each currency.
 */
export function toBaseMinor(
  minor: MoneyMinor,
  from: string | null | undefined,
  base: CurrencyCode,
  rate: number,
): MoneyMinor {
  const code = normalizeCurrency(from) ?? base;
  if (code === base) return minor;
  if (!Number.isFinite(rate) || rate <= 0) return minor;
  return convertMinor(minor, code, base, rate);
}

export function formatRate(rate: number): string {
  return `1 USD = ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate)} ZiG`;
}

export function sumMoney(values: readonly MoneyMinor[]): MoneyMinor {
  return values.reduce<number>((total, value) => total + value, 0);
}

/**
 * How a fee figure should read at a glance. Derived from the outstanding
 * balance and its due date, so the same amount is never green on one screen and
 * red on another.
 */
export type FeeStatus = 'paid' | 'credit' | 'overdue' | 'due_soon' | 'outstanding' | 'void';

export interface FeeStatusInput {
  /** Outstanding balance in minor units; negative means the family is in credit. */
  balanceMinor: MoneyMinor;
  /** Invoice due date, `YYYY-MM-DD`, when one exists. */
  dueDate?: string | null;
  /** Record status, so voided invoices never read as debt. */
  status?: string | null;
  /** Override "today" (`YYYY-MM-DD`) for tests and statements. */
  today?: string;
  /** Days before the due date that a balance starts reading as due soon. */
  dueSoonDays?: number;
}

const VOID_STATUSES = new Set(['void', 'voided', 'cancelled', 'canceled']);

export function classifyFee(input: FeeStatusInput): FeeStatus {
  const { balanceMinor, dueDate, status, dueSoonDays = 7 } = input;
  if (status && VOID_STATUSES.has(status.trim().toLowerCase())) return 'void';
  if (!Number.isFinite(balanceMinor)) return 'outstanding';
  if (balanceMinor < 0) return 'credit';
  if (balanceMinor === 0) return 'paid';

  const today = input.today ?? new Date().toISOString().slice(0, 10);
  if (!dueDate) return 'outstanding';
  const due = dueDate.slice(0, 10);
  if (due < today) return 'overdue';

  const dueTime = Date.parse(`${due}T00:00:00Z`);
  const todayTime = Date.parse(`${today}T00:00:00Z`);
  if (Number.isFinite(dueTime) && Number.isFinite(todayTime)) {
    const daysUntilDue = Math.round((dueTime - todayTime) / 86_400_000);
    if (daysUntilDue <= dueSoonDays) return 'due_soon';
  }
  return 'outstanding';
}

export function isPositiveMoney(minor: MoneyMinor): boolean {
  return Number.isInteger(minor) && minor > 0;
}
